import { useState, useCallback, useEffect } from "react";
import fleetApi from "@/lib/fleetApi";
import { useAuth } from "@/context/AuthContext";
import LiveMap, { driverColor, isStale } from "@/components/portal/LiveMap";
import { useLiveRefresh, UpdatedAgo, ago } from "@/lib/liveRefresh";
import { MapPin } from "lucide-react";

// Full-window live map, meant to be opened in its own browser window or a wall screen.
export default function LiveMapPage() {
  const { organization } = useAuth();
  const [live, setLive] = useState(null);
  const load = useCallback(() => fleetApi.get("/live-map").then((r) => setLive(Array.isArray(r.data) ? r.data : [])), []);
  useEffect(() => { load().catch(() => setLive([])); }, [load]);
  const at = useLiveRefresh(load, 15000);
  useEffect(() => { document.title = "Live map · RideStamp"; }, []);

  const drivers = (live || []).map((x) => ({ id: x.driver_id, name: x.name || x.full_name, lat: x.lat, lng: x.lng, last_ping: x.last_ping, marker: x.marker, trip: x.current_trip }));
  const located = drivers.filter((x) => x.last_ping);

  return (
    <div className="flex h-screen flex-col bg-[#0a0f1a] text-white" data-testid="live-map-page">
      <header className="flex flex-wrap items-center gap-3 border-b border-slate-800 px-4 py-2.5">
        <MapPin className="h-4 w-4 text-blue-400" />
        <h1 className="text-sm font-bold tracking-wide">Live GPS — {organization?.name || "Fleet"}</h1>
        <span className="font-mono-data text-xs text-slate-500" data-testid="live-map-page-count">{located.length} reporting · {drivers.length - located.length} no location</span>
        <span className="ml-auto"><UpdatedAgo at={at} testid="live-map-page-updated" /></span>
      </header>
      <div className="min-h-0 flex-1 p-3">
        <LiveMap drivers={drivers} onSelect={(id) => window.open(`/portal/drivers/${id}`, "_blank")} heightClass="h-full" scrollZoom />
      </div>
      <footer className="flex max-h-28 flex-wrap gap-2 overflow-y-auto border-t border-slate-800 px-4 py-2" data-testid="live-map-page-legend">
        {located.map((x) => (
          <span key={x.id} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 bg-slate-900 px-2.5 py-1 text-[11px] text-slate-200" data-testid={`live-map-page-driver-${x.id}`}>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: driverColor(x.id), opacity: isStale(x.last_ping) ? 0.5 : 1 }} />
            {x.name}<span className={isStale(x.last_ping) ? "text-amber-400" : "text-slate-500"}>{ago(x.last_ping)}</span>
            {x.trip?.external_trip_id && <span className="text-blue-300">· {x.trip.external_trip_id}</span>}
          </span>
        ))}
        {live && !located.length && <p className="text-xs text-slate-500" data-testid="live-map-page-none">No driver has shared a live position yet. Positions appear once a driver is on duty in the app with location allowed.</p>}
      </footer>
    </div>
  );
}
